import React, { useState } from 'react';
import { TrashIcon, ExclamationTriangleIcon, GlobeIcon, MobileIcon, Cross2Icon } from '@radix-ui/react-icons';

const DeleteModal = ({ topic, onClose, onDelete }) => {
    const [deleteFrom, setDeleteFrom] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Extract topic details
    const siteName = topic.wordpress_site?.site_name || topic.word_press_site?.site_name || 'WordPress';
    const isPublished = topic.publish_type === 'publish';
    const hasWordPressPost = !!topic.wordpress_post_id;
    const wordPressUrl = topic.wordpress_url;

    const handleDelete = async () => {
        if (!deleteFrom) {
            setError('Please select a delete option');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const authToken = localStorage.getItem('auth_token');

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/topics/${topic.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({ delete_from: deleteFrom })
            });

            const data = await response.json();

            if (data.success) {
                onDelete(data.message, data.deleted_from);
                onClose();
            } else {
                setError(data.message || 'Failed to delete article');
            }
        } catch (err) {
            setError('Error deleting article. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="relative w-full max-w-lg rounded-xl overflow-hidden p-[1px]">
                {/* TRAVELLING LIGHT (THE ENGINE) */}
                <div className="absolute top-[-50%] left-[-50%] h-[200%] w-[200%] animate-[spin_4s_linear_infinite] bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-75" />
                {/* INNER CONTENT (BLACK CARD) */}
                <div
                    className="relative z-10 bg-zinc-950 w-full rounded-xl max-h-[90vh] overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-800 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-zinc-700"
                    style={{ scrollbarColor: '#27272a transparent', scrollbarWidth: 'thin' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* CLOSE BUTTON */}
                    <button
                        type="button"
                        className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10 z-10"
                        onClick={onClose}
                        disabled={loading}
                    >
                        <Cross2Icon className="h-5 w-5" />
                    </button>

                    {/* HEADER */}
                    <div className="px-6 pt-8 pb-6 text-center">
                        <div className="bg-red-500/10 text-red-500 h-16 w-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                            <TrashIcon className="h-8 w-8" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Delete Topic?</h2>
                        {isPublished && hasWordPressPost && (
                            <p className="text-sm text-red-400 flex items-center justify-center gap-2">
                                <ExclamationTriangleIcon className="h-4 w-4" />
                                This topic is published live
                            </p>
                        )}
                    </div>

                    {/* BODY */}
                    <div className="px-6 pb-6 space-y-6">
                        {/* ARTICLE DETAILS */}
                        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider min-w-[80px]">Title</span>
                                    <span className="text-sm text-white text-left flex-1">{topic.topic_title}</span>
                                </div>
                                {hasWordPressPost && (
                                    <>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider min-w-[80px]">Site</span>
                                            <span className="text-sm text-zinc-300 text-left">{siteName}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider min-w-[80px]">Status</span>
                                            <span className={`text-sm font-semibold ${isPublished ? 'text-red-400' : 'text-zinc-300'} text-left`}>
                                                {isPublished ? '🔴 Live on WordPress' : 'Draft'}
                                            </span>
                                        </div>
                                        {wordPressUrl && (
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider min-w-[80px]">URL</span>
                                                <a
                                                    href={wordPressUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-red-400 hover:text-red-300 hover:underline break-all text-left flex-1"
                                                >
                                                    {wordPressUrl}
                                                </a>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* SELECTION CARDS */}
                        <div className="space-y-3">
                            <p className="text-sm text-zinc-400 text-center">Where do you want to delete this topic?</p>

                            {/* Card 1: App Only */}
                            <button
                                type="button"
                                onClick={() => setDeleteFrom('contentvertex_only')}
                                disabled={loading}
                                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${deleteFrom === 'contentvertex_only'
                                    ? 'border-violet-500 bg-violet-500/10 ring-2 ring-violet-500/30'
                                    : 'border-zinc-700 hover:border-zinc-500 bg-zinc-900 hover:bg-zinc-800'
                                    }`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="bg-zinc-800 p-2 rounded-lg">
                                        <MobileIcon className="h-5 w-5 text-zinc-400" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-semibold text-white">Delete from ContentVertex only</div>
                                        <div className="text-sm text-zinc-500 mt-1">
                                            {hasWordPressPost
                                                ? `Keep ${isPublished ? 'live post' : 'draft'} on ${siteName}`
                                                : 'Article not published to WordPress'}
                                        </div>
                                    </div>
                                </div>
                            </button>

                            {/* Card 2: Both */}
                            {hasWordPressPost && (
                                <button
                                    type="button"
                                    onClick={() => setDeleteFrom('both')}
                                    disabled={loading}
                                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${deleteFrom === 'both'
                                        ? 'border-red-900/50 bg-red-500/5'
                                        : 'border-red-500/30 hover:border-red-500 bg-red-500/5 hover:bg-red-500/10'
                                        }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 rounded-lg bg-red-500/10">
                                            <GlobeIcon className={`h-5 w-5 ${isPublished ? 'text-red-500' : 'text-red-400'}`} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="font-semibold text-white">Delete from Both</div>
                                            <div className="text-sm text-zinc-500 mt-1">ContentVertex + {siteName}</div>
                                            {isPublished && (
                                                <div className="mt-3 px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg flex items-start gap-2">
                                                    <ExclamationTriangleIcon className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                                                    <span className="text-xs text-red-300 font-medium">
                                                        WARNING: This will remove the LIVE post from your WordPress site!
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            )}
                        </div>

                        {/* ERROR MESSAGE */}
                        {error && (
                            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                                {error}
                            </div>
                        )}
                    </div>

                    {/* FOOTER */}
                    <div className="px-6 py-4 bg-zinc-900/50 border-t border-white/10 flex gap-3">
                        <button
                            type="button"
                            className="flex-1 px-4 py-3 text-sm font-medium text-zinc-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 ${(!deleteFrom || loading)
                                ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                                : 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-500/20'
                                }`}
                            onClick={handleDelete}
                            disabled={!deleteFrom || loading}
                        >
                            {loading ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteModal;
